import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  GraduationCap,
  Clock,
  Calendar,
  User,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  LogIn,
  LogOut,
  Loader2,
  Briefcase,
  Send,
  History,
  Download,
  CalendarDays,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const InternDashboardPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [checkingIn, setCheckingIn] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({
    request_type: 'absence',
    date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    reason: ''
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/intern/dashboard');
      setDashboard(res.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      const action = dashboard?.today_checked_in ? 'check_out' : 'check_in';
      const res = await api.post(`/manifest/interns/${dashboard?.intern?.id}/check-in`, { action });
      toast.success(res.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du pointage');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.date || !requestForm.reason) {
      toast.error('Date et raison requises');
      return;
    }
    
    setSubmittingRequest(true);
    try {
      await api.post(`/manifest/interns/${dashboard?.intern?.id}/absence-request`, requestForm);
      toast.success('Demande envoyée');
      setShowRequestModal(false);
      setRequestForm({
        request_type: 'absence',
        date: new Date().toISOString().split('T')[0],
        start_time: '',
        end_time: '',
        reason: ''
      });
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const formatScheduleDay = (daySchedule) => {
    if (!daySchedule) return 'Non défini';
    return `${daySchedule.start || '09:00'} - ${daySchedule.end || '17:00'}`;
  };

  const getRequestStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">En attente</Badge>;
      case 'approved':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Approuvée</Badge>;
      case 'rejected':
        return <Badge variant="secondary" className="bg-red-100 text-red-800">Refusée</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  const intern = dashboard?.intern;
  const supervisor = dashboard?.supervisor;
  const agency = dashboard?.agency;

  return (
    <MainLayout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
              {intern?.first_name?.[0]}{intern?.last_name?.[0]}
            </div>
            <div>
              <h1 className="text-2xl font-bold">Bonjour, {intern?.first_name} !</h1>
              <p className="text-slate-500">
                {intern?.department || 'Stagiaire'} • {intern?.school}
              </p>
            </div>
          </div>
          
          {/* Check-in/out button */}
          <Button
            onClick={handleCheckIn}
            disabled={checkingIn}
            size="lg"
            className={cn(
              "gap-2",
              dashboard?.today_checked_in 
                ? "bg-orange-500 hover:bg-orange-600" 
                : "bg-green-500 hover:bg-green-600"
            )}
          >
            {checkingIn ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : dashboard?.today_checked_in ? (
              <><LogOut className="w-5 h-5" /> Pointer ma sortie</>
            ) : (
              <><LogIn className="w-5 h-5" /> Pointer mon arrivée</>
            )}
          </Button>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Début stage</p>
                  <p className="font-semibold">{new Date(intern?.start_date).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <CalendarDays className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Fin stage</p>
                  <p className="font-semibold">{new Date(intern?.end_date).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Projets</p>
                  <p className="font-semibold">{dashboard?.assigned_projects?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "p-2 rounded-lg",
                  intern?.is_present ? "bg-green-100" : "bg-slate-100"
                )}>
                  <CheckCircle2 className={cn(
                    "w-5 h-5",
                    intern?.is_present ? "text-green-600" : "text-slate-400"
                  )} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Statut</p>
                  <p className="font-semibold">{intern?.is_present ? 'Présent' : 'Absent'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview" className="gap-1">
              <GraduationCap className="w-4 h-4 hidden sm:inline" />
              Aperçu
            </TabsTrigger>
            <TabsTrigger value="schedule" className="gap-1">
              <Clock className="w-4 h-4 hidden sm:inline" />
              Horaires
            </TabsTrigger>
            <TabsTrigger value="attendance" className="gap-1">
              <History className="w-4 h-4 hidden sm:inline" />
              Présences
            </TabsTrigger>
            <TabsTrigger value="projects" className="gap-1">
              <Briefcase className="w-4 h-4 hidden sm:inline" />
              Projets
            </TabsTrigger>
            <TabsTrigger value="requests" className="gap-1">
              <ClipboardList className="w-4 h-4 hidden sm:inline" />
              Demandes
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Agency info */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    L'agence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="font-semibold text-lg">{agency?.name}</p>
                  {agency?.address && (
                    <p className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4" />
                      {agency.address}, {agency.city}
                    </p>
                  )}
                  {agency?.phone && (
                    <p className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4" />
                      {agency.phone}
                    </p>
                  )}
                  {agency?.email && (
                    <p className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="w-4 h-4" />
                      {agency.email}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Supervisor info */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Mon tuteur
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {supervisor ? (
                    <>
                      <p className="font-semibold text-lg">
                        {supervisor.first_name} {supervisor.last_name}
                      </p>
                      {supervisor.email && (
                        <p className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="w-4 h-4" />
                          {supervisor.email}
                        </p>
                      )}
                      {supervisor.phone && (
                        <p className="flex items-center gap-2 text-sm text-slate-600">
                          <Phone className="w-4 h-4" />
                          {supervisor.phone}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-500">Non assigné</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Documents */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Mes documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {intern?.documents?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {intern.documents.map((doc, idx) => (
                      <a
                        key={doc.id || idx}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <FileText className="w-5 h-5 text-blue-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{doc.name || 'Document'}</p>
                          <p className="text-xs text-slate-500">
                            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : ''}
                          </p>
                        </div>
                        <Download className="w-4 h-4 text-slate-400" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucun document</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-4 mt-4">
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Mes horaires de travail
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map((day) => {
                    const dayLabels = {
                      monday: 'Lundi',
                      tuesday: 'Mardi',
                      wednesday: 'Mercredi',
                      thursday: 'Jeudi',
                      friday: 'Vendredi'
                    };
                    const daySchedule = intern?.schedule?.[day];
                    return (
                      <div key={day} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium">{dayLabels[day]}</span>
                        <span className={cn(
                          "text-sm",
                          daySchedule ? "text-slate-700" : "text-slate-400"
                        )}>
                          {formatScheduleDay(daySchedule)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="space-y-4 mt-4">
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Historique des présences
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.recent_attendance?.length > 0 ? (
                  <div className="space-y-2">
                    {dashboard.recent_attendance.slice().reverse().map((att, idx) => (
                      <div key={att.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{new Date(att.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                          <p className="text-sm text-slate-500">
                            {att.check_in && `Arrivée: ${att.check_in}`}
                            {att.check_out && ` • Départ: ${att.check_out}`}
                            {att.hours_worked && ` • ${att.hours_worked}h`}
                          </p>
                        </div>
                        <Badge variant={att.status === 'present' ? 'default' : 'secondary'} className={cn(
                          att.status === 'present' ? 'bg-green-100 text-green-800' : 
                          att.status === 'absent' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'
                        )}>
                          {att.status === 'present' ? 'Présent' : att.status === 'absent' ? 'Absent' : att.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucun historique</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4 mt-4">
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Mes projets
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.assigned_projects?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.assigned_projects.map((project) => (
                      <a
                        key={project.id}
                        href={`/projects/${project.id}`}
                        className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <div>
                          <p className="font-semibold">{project.name}</p>
                          <p className="text-sm text-slate-500">{project.client_name}</p>
                        </div>
                        <Badge>{project.status}</Badge>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucun projet assigné</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowRequestModal(true)} className="gap-2">
                <Send className="w-4 h-4" />
                Nouvelle demande
              </Button>
            </div>
            
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Mes demandes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.pending_requests?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.pending_requests.map((req) => (
                      <div key={req.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">
                              {req.request_type === 'absence' ? 'Absence' : 
                               req.request_type === 'early_departure' ? 'Départ anticipé' : 
                               'Arrivée tardive'}
                            </p>
                            <p className="text-sm text-slate-500">
                              {new Date(req.date).toLocaleDateString('fr-FR')}
                              {req.start_time && ` à ${req.start_time}`}
                            </p>
                            <p className="text-sm mt-1">{req.reason}</p>
                          </div>
                          {getRequestStatusBadge(req.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucune demande en cours</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Request Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle demande</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Type de demande</Label>
              <Select
                value={requestForm.request_type}
                onValueChange={(v) => setRequestForm({ ...requestForm, request_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="absence">Absence</SelectItem>
                  <SelectItem value="early_departure">Départ anticipé</SelectItem>
                  <SelectItem value="late_arrival">Arrivée tardive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={requestForm.date}
                onChange={(e) => setRequestForm({ ...requestForm, date: e.target.value })}
              />
            </div>
            
            {(requestForm.request_type === 'early_departure' || requestForm.request_type === 'late_arrival') && (
              <div>
                <Label>Heure</Label>
                <Input
                  type="time"
                  value={requestForm.start_time}
                  onChange={(e) => setRequestForm({ ...requestForm, start_time: e.target.value })}
                />
              </div>
            )}
            
            <div>
              <Label>Raison</Label>
              <Textarea
                value={requestForm.reason}
                onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                placeholder="Expliquez la raison de votre demande..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmitRequest} disabled={submittingRequest}>
              {submittingRequest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default InternDashboardPage;
